import { AdvancedTrackLead } from '../../api/types'
import { WeeklyPlannerData } from './WeeklyContentPlanner'
import {
  CarePlanState,
  CarePlanStatus,
  applyAddCustomOption,
  applyMoveItem,
  applyPlaceItem,
  applyPrescribeSurvey,
  applyRemoveItem,
  applyRemoveItemsByOption,
  applyRemoveTrackItems,
  applySetItemComment,
  applySetItemLead,
  applyScheduleItem,
  applySetSelectedOptions,
  emptyMonths,
} from './carePlanState'
import { TrackRow, interactionFor } from './categories'

type SetState = (next: CarePlanState | ((prev: CarePlanState) => CarePlanState), options?: { save?: boolean }) => void

// Thin binds of the `carePlanState` reducers to the persisted setter.
export type CarePlanActions = ReturnType<typeof carePlanActions>

export const carePlanActions = (state: CarePlanState, setState: SetState, trackRows: TrackRow[] = []) => {
  const itemsIn = (rowKey: string) => state.itemsByRowKey?.[rowKey] ?? []

  return {
    setNotesHtml: (notesHtml: string) => setState((prev) => ({ ...prev, notesHtml })),

    setStatus: (status: CarePlanStatus) => setState((prev) => ({ ...prev, status })),

    setWeeklyPlanner: (weeklyPlanner: WeeklyPlannerData) => setState((prev) => ({ ...prev, weeklyPlanner })),

    // A view change, not a plan edit: saving it would add a revision and conflict with other editors.
    toggleCategory: (categoryId: string) =>
      setState(
        (prev) => {
          const expanded = prev.expandedCategoryIds ?? []
          return {
            ...prev,
            expandedCategoryIds: expanded.includes(categoryId)
              ? expanded.filter((id) => id !== categoryId)
              : [...expanded, categoryId],
          }
        },
        { save: false }
      ),

    setCell: (rowKey: string, monthIndex: number, value: string) =>
      setState((prev) => ({
        ...prev,
        cellsByRowKey: {
          ...prev.cellsByRowKey,
          [rowKey]: (prev.cellsByRowKey?.[rowKey] ?? emptyMonths()).map((month, index) =>
            index === monthIndex ? value : month
          ),
        },
      })),

    cellAt: (rowKey: string, monthIndex: number): string => state.cellsByRowKey?.[rowKey]?.[monthIndex] ?? '',

    itemsIn,

    optionsFor: (rowKey: string): string[] => {
      const interaction = interactionFor(rowKey, trackRows)
      const base = interaction?.kind === 'options' ? interaction.options : []
      return [...base, ...(state.customOptionsByRowKey?.[rowKey] ?? [])]
    },

    selectedIn: (rowKey: string): string[] => itemsIn(rowKey).map((item) => item.option),

    placeItem: (rowKey: string, startMonth: number, option: string, months: number, trackKey?: string) =>
      setState((prev) => applyPlaceItem(prev, rowKey, startMonth, option, months, trackKey)),

    scheduleItem: (rowKey: string, itemId: string, startWeek: number) =>
      setState((prev) => applyScheduleItem(prev, rowKey, itemId, startWeek)),

    setItemLead: (rowKey: string, itemId: string, lead: AdvancedTrackLead) =>
      setState((prev) => applySetItemLead(prev, rowKey, itemId, lead)),

    removeTrackItems: (rowKey: string, trackKey: string) =>
      setState((prev) => applyRemoveTrackItems(prev, rowKey, trackKey)),

    prescribeSurvey: (startWeek: number, option: string) =>
      setState((prev) => applyPrescribeSurvey(prev, startWeek, option)),

    removeItem: (rowKey: string, itemId: string) => setState((prev) => applyRemoveItem(prev, rowKey, itemId)),

    removeItemsByOption: (rowKey: string, option: string) =>
      setState((prev) => applyRemoveItemsByOption(prev, rowKey, option)),

    moveItem: (rowKey: string, itemId: string, startMonth: number) =>
      setState((prev) => applyMoveItem(prev, rowKey, itemId, startMonth)),

    addCustomOption: (rowKey: string, option: string) => setState((prev) => applyAddCustomOption(prev, rowKey, option)),

    setSelectedOptions: (rowKey: string, nextSelected: string[]) =>
      setState((prev) => applySetSelectedOptions(prev, rowKey, nextSelected)),

    setItemComment: (rowKey: string, itemId: string, comment: string) =>
      setState((prev) => applySetItemComment(prev, rowKey, itemId, comment)),
  }
}
